import * as colors from 'scoreboard-colors'
import * as d3 from 'd3-selection'

/*
 * Returns the actual width of the text in the given node.
 */
const getTextWidth = (html, node) => {
  const styles = window.getComputedStyle(node)

  const canvas = getTextWidth.canvas || (getTextWidth.canvas = document.createElement('canvas'))
  const context = canvas.getContext('2d')
  context.font = `${styles['font-weight']} ${styles['font-size']} ${styles['font-family']}`

  // Create a temporary element to parse the HTML
  const temp = document.createElement('div')
  temp.innerHTML = html

  // Extract text content, removing all HTML tags
  const text = temp.textContent || temp.innerText || ''
  return context.measureText(text).width
}

const ellipse = '...'

const widthBasedClamp = (d, node) => {
  // If the text is too wide, we'll truncate it and add an ellipse
  let text = getTextWidth(d.value, node) > d.container.width ? (d.truncatedValue || d.value) : d.value

  while (getTextWidth(`${text}`, node) > d.container.width && text.length > 0) {
    text = text.slice(0, -1) // Remove last character
    node.innerHTML = `${text}${ellipse}`
  }
  return node
}

const heightBasedClamp = (d, node) => {
  let text = d.value
  let truncated = false

  while (node.offsetHeight > d.container.height && text.length > 0) {
    text = text.slice(0, -1)
    node.innerHTML = `${text}${ellipse}`
    truncated = true
  }

  if (truncated) {
    node.innerHTML = `${text.slice(0, Math.max(text.length - 3, 0))}${ellipse}`
  }

  return this
}

/*
 * Clamps the text to the dimensions of the container.
 * We have to use this JS implementation b/c there's a bug in chrome when text is text-align: right.
 * Hopefully it's fixed at some point and we can simply use css. Use https://jsfiddle.net/zeLwtuns/ to test.
 */
const clamp = selection => {
  selection.each(function (d) {
    // Reset the text to the text in case it the original doesn't fit and we're using a defined `truncatedValue`
    this.innerHTML = this.offsetHeight > d.container.height ? (d.truncatedValue || d.value) : d.value

    if (this.offsetHeight == d.fontSize) {
      // If the text is only one line, we can use the width-based clamp
      return widthBasedClamp(d, this)
    } else {
      // If the text is multiple lines, we'll use the height-based clamp
      return heightBasedClamp(d, this)
    }
  })
}

/*
 * Get the rotation of the text. If the rotation is not a number, we'll assume it's `auto`.
 * If it's auto, we'll look at the container rotation and adjust the text rotation so it's always readable.
 * i.e. never upside down or sideways.
 */
const getRotation = d => {
  let rotation = d.text.rotation

  // If rotation is not a number, we'll assume it's auto
  if (isNaN(rotation)) {
    const angle = d.dimensions.angle
    // Look at the container rotation and adjust the text rotation accordingly
    if (angle > 45 && angle < 135) {
      rotation = 270
    } else if (angle > 135 && angle < 225) {
      rotation = 180
    } else if (angle > 225 && angle < 315) {
      rotation = 90
    } else {
      rotation = 0
    }
  }

  return rotation
}

/*
 * Get the outer text container. This is the dimensions of the symbol minus 10% padding.
 *
 * When there isn't a stroke or fill, we'll use 0% padding. See backstory on
 * https://github.com/SpiderStrategies/Scoreboard/issues/59784
 */
const getOuterTextContainer = (dimensions, padding = .1) => {
  const xPadding = dimensions.width * padding
  const yPadding = dimensions.height * padding
  const width = dimensions.width - xPadding * 2
  const height = dimensions.height - yPadding * 2

  const outer = {
    width,
    height,
    center: [
      dimensions.width * padding + width / 2,
      dimensions.height * padding + height / 2,
    ]
  }
  return outer
}

/*
  * Get the text container. If the symbol has a `textContainer` method, we'll use that.
  * Otherwise we'll use the default container which is the dimensions of the symbol.
  */
const getTextContainer = (d, symbol, strokeWidth, fill) => {
  const visibleShape = strokeWidth || fill !== 'none' // Determine if we're actually showing a shape. A shape isn't visible if it doesn't have a fill or stroke.
  const rotation = getRotation(d)
  // Subtract the stroke width from the dimensions so the text doesn't overlap the symbol's border
  const width = Math.max(0, d.dimensions.width - strokeWidth)
  const height = Math.max(0, d.dimensions.height - strokeWidth)

  // The default dimensions center ignores stroke offset. We'll account for it when we
  // position the foreignObject text.
  const dimensions = Object.assign({width, height}, {
    center: [
      width / 2,
      height / 2,
    ],
  })

  const padding = strokeWidth === 0 && fill === 'none' ? 0 : .1
  const outer = getOuterTextContainer(dimensions, padding)

  // If the symbol has a textContainer method, we'll use that to determine the container.
  // But only if the symbol is visible.
  const inner = visibleShape && symbol.textContainer?.(dimensions, d.handles.map(handle => handle.coords))
  const lineHeight = d.text.fontSize
  const widthThreshold = 5 // 5x the line height

  /*
   * Use the inner container if it's defined and the height is greater than the line height and the width is greater than `widthThresold` * the line height.
   * Otherwise, bust out and use the outer container.
   */
  const container = (inner?.height > lineHeight && inner.width > (lineHeight * widthThreshold)) ? inner : outer

  // Check if we're rotating the text, if so we need to adjust the container
  if (rotation == 90 || rotation == 270) {
    const w = container.width
    const h = container.height

    // Swap width and height
    container.width = h
    container.height = w
  }

  container.rotation = rotation

  return container
}

export default function (selection, symbol, strokeWidth = 0, fill) {
  selection.each(function (d) {
    let text = d3.select(this).selectAll('foreignObject')
      .data(d => {
        if (symbol.noText) {
          return []
        }
        if (!d.text) {
          return []
        }

        const container = getTextContainer(d, symbol, strokeWidth, fill)

        return [Object.assign({}, d.text, {
          container,
          textVariation: colors.getTextVariation(d.color, d.opacity),
          color: d.text?.color && d.text.color, // Set the color directly if it's defined
        })]
      })

    text.exit().remove()

    let enter = text.enter()
      .insert('foreignObject', 'path.symbol:last-of-type + *') // The text element needs to be after all the symbol paths
      .style('pointer-events', 'none') // Don't capture events on the text, let them pass through to the underlying symbol. This allows for hover effects on the symbol.

    enter.append('xhtml:div')
      .style('display', 'flex')
      .style('flex-direction', 'column')
      .style('justify-content', 'center') // vertical center
      .style('width', '100%')
      .style('height', '100%')
      .style('overflow', 'hidden')
      .append('xhtml:p')
        .style('margin', 0)
        .style('padding', 0)

    text = text.merge(enter)

    text
      .attr('transform', d => `rotate(${d.container.rotation || 0} ${d.container.center[0]} ${d.container.center[1]})`)
      .attr('x', d => d.container.center[0] - d.container.width / 2 + strokeWidth / 2)
      .attr('y', d => d.container.center[1] - d.container.height / 2 + strokeWidth / 2)
      .attr('width', d => d.container.width)
      .attr('height', d => d.container.height)
      .select('p')
      .attr('class', d => {
        if (d.color) {
          return '' // No label-on-colored-background class needed, we're using the set color directly.
        } else {
          return `label-on-colored-background ${d.textVariation}`
        }
      })
      .style('text-align', d => d.align || 'center')
      .style('font-style', d => d.styles?.includes('italic') ? 'italic' : null)
      .style('font-weight', d => d.styles?.includes('bold') ? 'bold' : null)
      .style('text-decoration', d => d.styles?.includes('underline') ? 'underline' : null)
      .style('font-size', d => `${d.fontSize}px`)
      .style('color', d => d.color)
      .text(d => d.value)
      .call(clamp)
  })
}
